// context/app-context.tsx
"use client";

import { createContext, useContext, useMemo } from "react";

type DeviceInfo = {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isBot: boolean;
  os: string;
  browser: string;
};

type AppContextType = {
  // Server-provided values (all read-only)
  userID: string;
  userClass: string;
  nonce: string;
  isDeveloperZone: boolean;
  inAppMode: boolean;
  device: DeviceInfo;
  countryCode: string;
  platform: string;
  url: string;
  referrer: string | null;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({
  children,
  // All props come from server
  userID,
  userClass,
  nonce,
  isDeveloperZone,
  inAppMode,
  device,
  countryCode,
  platform,
  url,
  referrer,
}: AppContextType & { children: React.ReactNode }) {
  const value = useMemo(
    () => ({
      userID,
      userClass,
      nonce,
      isDeveloperZone,
      inAppMode,
      device,
      countryCode,
      platform,
      url,
      referrer,
    }),
    [
      userID,
      userClass,
      nonce,
      isDeveloperZone,
      inAppMode,
      device,
      countryCode,
      platform,
      url,
      referrer,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
}
